import React from "react";
import { Button, Modal } from "../../../../../components/core";
import { useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import AssignToPartner from "../../../assign-to-partner";
import { useDispatch, useSelector } from "react-redux";
import { handleOnChanged } from "../../../../../features/assign-to-partner/assign-to-partner-slice";
import TotalRecords from "../../../../../components/core/total-records";
import { Chip } from "primereact/chip";
import { Badge } from "primereact/badge";
import { Row } from "primereact/row";
import { ColumnGroup } from "primereact/columngroup";

// same values on global admin
export const LOCKER_STATE_DROPDOWN = [
  {
    label: "In Storage",
    value: -1,
  },
  {
    label: "Being Installed",
    value: 1,
  },
  {
    label: "Live",
    value: 2,
  },
  {
    label: "Under Maintenance",
    value: 3,
  },
  {
    label: "Deactivated",
    value: 4,
  },
];

export const lockerStatusTemplate = (rowData) => {
  return (
    <Chip
      className={`text-[13px] text-secondary-purpleGray font-bold ${
        rowData.lockerStatus === 1 ? "bg-green-400" : "bg-red-300"
      } ${rowData.lockerName === "Total" && "hidden"}`}
      label={rowData.lockerStatus === 1 ? "On" : "Off"}
    />
  );
};

export default function LockerTable({ refetch, disabled = false, items = [] }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const accessPermission = useSelector(
    (state) => state.accessPermissions.accessPermissions
  );
  const [showAssignToPartner, setShowAssignToPartner] = React.useState(false);
  const [currentLockerTypeId, setCurrentLockerTypeId] = React.useState();
  const [currentLockerId, setCurrentLockerId] = React.useState();
  const [validation, setValidation] = React.useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });

  // Column templates

  const lockerStateTemplate = (rowData) => {
    const adjustedLockerStateNo =
      rowData.lockerStateNo === 0 ? -1 : rowData.lockerStateNo;
    const state = LOCKER_STATE_DROPDOWN.find(
      (val) => val.value === adjustedLockerStateNo
    );

    const whichColor = (stateValue) => {
      switch (stateValue) {
        case -1:
          return "bg-primary-primaryYellow text-black";
        case 1:
          return "bg-orange-400 text-secondary-purpleGray";
        case 2:
          return "bg-green-400 text-secondary-purpleGray";
        case 3:
          return "bg-blue-400 text-secondary-purpleGray";
        case 4:
          return "bg-gray-400 text-secondary-purpleGray";
        default:
          return "bg-yellow-400 text-secondary-purpleGray";
      }
    };

    if (!state) {
      return <div>-</div>;
    }

    return (
      <Badge
        value={state.label}
        className={`h-full text-[13px] ${whichColor(state.value)}`}
      />
    );
  };

  const lockerTypeTemplate = (rowData) => {
    return `(${rowData.lockerType})`;
  };

  // Separate templates for each service column
  const partnerTemplate = (rowData) => {
    if (accessPermission.partners === 0 || accessPermission.partners === "0")
      return null;

    if (rowData.serviceBy.length === 0) {
      return <div></div>;
    }

    return (
      <div>
        {rowData.serviceBy.map((serviceData, serviceKey) => (
          <div key={serviceKey} className="my-2">
            {serviceData.companyNickname}
          </div>
        ))}
      </div>
    );
  };

  const branchTemplate = (rowData) => {
    if (accessPermission.branches === 0 || accessPermission.branches === "0")
      return null;

    if (rowData.serviceBy.length === 0) {
      return (
        <div className="grid grid-cols-4 gap-2">
          <p className="font-bold text-red-500 col-span-3 text-center">
            No Courier Branch assigned
          </p>
        </div>
      );
    }

    return (
      <div>
        {rowData.serviceBy.map((serviceData, serviceKey) => (
          <div key={serviceKey} className="grid grid-cols-2 gap-2 my-1">
            <p className="my-auto">{serviceData.partnerBranch}</p>
            <p className="my-auto">
              Agents:{" "}
              <b className={serviceData.agents === 0 ? "text-red-500" : ""}>
                {serviceData.agents}
              </b>
            </p>
          </div>
        ))}
      </div>
    );
  };

  const assignedAgentsTemplate = (rowData) => {
    if (accessPermission.lockers === 1 || accessPermission.lockers === "1")
      return null;

    if (rowData.serviceBy.length === 0) {
      return (
        <Button
          value="Assign to partner"
          className="p-2"
          disabled={disabled}
          onClick={() => {
            setShowAssignToPartner(true);
            setCurrentLockerTypeId(rowData.lockerTypeId);
            setCurrentLockerId(rowData.lockerId);
          }}
        />
      );
    }

    return (
      <div>
        {rowData.serviceBy.map((serviceData, serviceKey) => (
          <div key={serviceKey}>
            {serviceData.agents === 0 && (
              <Button
                value="Email notice"
                className="p-2 grid grid-cols-1 gap-2 my-1"
                disabled={true}
              />
            )}
          </div>
        ))}
        <Button
          value="Assign to partner"
          className="p-1 text-center"
          disabled={disabled}
          onClick={() => {
            setShowAssignToPartner(true);
            setCurrentLockerTypeId(rowData.lockerTypeId);
            setCurrentLockerId(rowData.lockerId);
          }}
        />
      </div>
    );
  };

  // Separate door templates
  const occupiedTemplate = (rowData) => {
    return (
      <div className="text-center">{rowData.doors.occupiedDoors || 0}</div>
    );
  };

  const availableTemplate = (rowData) => {
    return (
      <div className="text-center">{rowData.doors.availableDoors || 0}</div>
    );
  };

  const maintenanceTemplate = (rowData) => {
    return (
      <div className="text-center">{rowData.doors.unavailableDoors || 0}</div>
    );
  };

  const headerGroup = (
    <ColumnGroup>
      <Row>
        <Column
          header="Locker Status"
          field="lockerStatus"
          headerClassName="text-center border border-gray-400"
          colSpan={1}
          headerStyle={{ backgroundColor: "#FECD08" }}
          rowSpan={2}
          sortable
          frozen
        />
        <Column
          header="Locker State"
          field="lockerStateNo"
          headerClassName="text-center border border-gray-400"
          colSpan={1}
          headerStyle={{ backgroundColor: "#FECD08" }}
          rowSpan={2}
          sortable
          frozen
        />
        <Column
          header="Locker Name"
          field="lockerName"
          headerClassName="text-center border border-gray-400"
          colSpan={1}
          headerStyle={{ backgroundColor: "#FECD08" }}
          rowSpan={2}
          sortable
          frozen
        />
        <Column
          header="Locker Type"
          field="lockerType"
          headerStyle={{ backgroundColor: "#FECD08" }}
          headerClassName="text-center border border-gray-400"
          colSpan={1}
          rowSpan={2}
          sortable
        />
        <Column
          header="Serviced By"
          field="servicedBy"
          headerClassName="text-center border border-gray-400"
          alignHeader={"center"}
          colSpan={3}
          headerStyle={{ backgroundColor: "#FECD08" }}
        />
        <Column
          header="Door Status"
          field="doorStatus"
          headerClassName="text-center border border-gray-400"
          alignHeader={"center"}
          colSpan={3}
          headerStyle={{ backgroundColor: "#FECD08" }}
        />
        {/* <Column
          header="Remarks / Comments"
          field="remarks"
          alignHeader={"center"}
          colSpan={1}
          rowSpan={2}
          headerStyle={{ backgroundColor: "#FECD08" }}
        /> */}
        {/* <Column
          header=""
          field="action"
          alignHeader={"center"}
          colSpan={1}
          rowSpan={2}
          // hidden={isDisabled}
          headerStyle={{ backgroundColor: "#FECD08" }}
        /> */}
      </Row>
      <Row>
        <Column
          header="Partner"
          field="companyNickname"
          alignHeader={"center"}
          headerClassName="text-center border border-gray-400"
          headerStyle={{ backgroundColor: "#FECD08" }}
        />
        <Column
          header="Branch"
          field="email"
          headerStyle={{ backgroundColor: "#FECD08" }}
          alignHeader={"center"}
          headerClassName="text-center border border-gray-400"
        />
        <Column
          header="Agents"
          field="agents"
          headerStyle={{ backgroundColor: "#FECD08" }}
          alignHeader={"center"}
          headerClassName="text-center border border-gray-400"
        />
        {/* <Column
          header="Partner Branch"
          field="partnerBranch"
          headerClassName="text-center"
          headerStyle={{ backgroundColor: "#FECD08" }}
        /> */}

        <Column
          header="Occupied"
          field="occupied"
          sortable
          headerStyle={{ backgroundColor: "#FECD08" }}
          alignHeader={"center"}
          headerClassName="text-center border border-gray-400"
        />
        <Column
          header="Available"
          field="available"
          headerStyle={{ backgroundColor: "#FECD08" }}
          alignHeader={"center"}
          headerClassName="text-center border border-gray-400"
          sortable
        />
        <Column
          header="Under Maintenance"
          alignHeader={"center"}
          headerClassName="text-center border border-gray-400"
          headerStyle={{ backgroundColor: "#FECD08" }}
          field="underMaintenance"
          sortable
        />
      </Row>
    </ColumnGroup>
  );

  return (
    <div>
      <TotalRecords count={items.length} />

      <DataTable
        value={items}
        scrollable
        scrollHeight="700px"
        rows={items?.length}
        stripedRows={true}
        emptyMessage="No lockers found"
        headerColumnGroup={headerGroup}
        footer={
          <div className="bg-gray-100 py-2 border-t-2 text-sm text-gray-600">
            <div className="flex justify-end items-center">
              <div>
                <strong>
                  {(() => {
                    const activeDoors = items.reduce((sum, item) => {
                      // Check if locker is On (status = 1) and Live or Being Installed (state = 1 or 2)
                      if (
                        item.lockerStatus === 1 &&
                        (item.lockerStateNo === 1 || item.lockerStateNo === 2)
                      ) {
                        return (
                          sum +
                          (item.doors.occupiedDoors || 0) +
                          (item.doors.availableDoors || 0)
                        );
                      }
                      return sum;
                    }, 0);

                    const totalDoors = items.reduce(
                      (sum, item) =>
                        sum +
                        (item.doors.occupiedDoors || 0) +
                        (item.doors.availableDoors || 0) +
                        (item.doors.unavailableDoors || 0),
                      0
                    );

                    return (
                      <div>
                        Total Doors:{" "}
                        <strong>
                          {activeDoors} / {totalDoors}
                        </strong>
                      </div>
                    );
                  })()}
                </strong>
              </div>
            </div>
          </div>
        }
      >
        <Column
          field="lockerStatus"
          header="Locker Status"
          body={lockerStatusTemplate}
          className="text-center p-2 border-r-2"
        />
        <Column
          field="lockerStateNo"
          body={lockerStateTemplate}
          className="text-center p-2 border-r-2"
        />
        <Column field="lockerName" className="text-left p-2 border-r-2" />
        <Column
          field="lockerType"
          body={lockerTypeTemplate}
          className="text-left p-2 border-r-2"
        />

        {/* Conditionally render service columns */}
        {!(
          accessPermission.partners === 0 || accessPermission.partners === "0"
        ) && (
          <Column body={partnerTemplate} className="text-left p-2 border-r-2" />
        )}

        {!(
          accessPermission.branches === 0 || accessPermission.branches === "0"
        ) && (
          <Column body={branchTemplate} className="text-left p-2 border-r-2" />
        )}

        {!(
          accessPermission.lockers === 1 || accessPermission.lockers === "1"
        ) && (
          <Column
            body={assignedAgentsTemplate}
            className="text-left p-2 border-r-2"
          />
        )}

        {/* Door columns */}
        <Column
          body={occupiedTemplate}
          className="text-center p-2 border-r-2"
        />
        <Column
          body={availableTemplate}
          className="text-center p-2 border-r-2"
        />
        <Column
          body={maintenanceTemplate}
          className="text-center p-2 border-r-2"
        />
      </DataTable>

      <Dialog
        visible={showAssignToPartner}
        onHide={() => setShowAssignToPartner(false)}
        header="LOCKER - PARTNER ASSIGNMENTS"
      >
        <AssignToPartner
          lockerTypeId={currentLockerTypeId}
          lockerId={currentLockerId}
          passBranchToReset1=""
          onSave={() => {
            setValidation({
              icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
              title: "Success",
              message: "Successfully assigned locker to a branch.",
              open: true,
              actions: (
                <div className="w-full">
                  <Button
                    onClick={() => setValidation({ open: false })}
                    value="OK"
                    className="p-2 w-full"
                  />
                </div>
              ),
            });
            if (refetch) {
              refetch();
            }
            setShowAssignToPartner(false);
          }}
        />
      </Dialog>

      <Modal
        icon={validation.icon}
        title={validation.title}
        message={validation.message}
        open={validation.open}
        actions={validation.actions}
      />
    </div>
  );
}
